import { useQuery } from "@tanstack/react-query";
import { Plus, Radio as RadioIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { CreateRadioDialog } from "../components/CreateRadioDialog";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { ProviderTabs } from "../components/ProviderTabs";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { radioEngine } from "../lib/RadioEngine";
import { spotifyApi } from "../lib/spotify";
import { useLocalRadioStore } from "../stores/LocalRadioStore";
import { usePlayerStore } from "../stores/playerStore";
import { MediaItemProps, RadioStation, Track } from "../types";

export const Radio = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const { isAuthenticated, login } = useSpotifyAuth();
  const { radioStations, initialize } = useLocalRadioStore();
  const { setCurrentTrack, setIsPlaying, setQueue } = usePlayerStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ["recommendations"],
    queryFn: async () => {
      const topTracks = await spotifyApi.currentUser.topItems("tracks", "short_term");
      return spotifyApi.recommendations.get({
        seed_tracks: topTracks.items.slice(0, 5).map((track) => track.id),
      });
    },
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const handleCreateRadioStation = async (name: string, seedTracks: Track[]) => {
    const newStation = await radioEngine.createRadioStation(name, seedTracks);
    setIsCreateDialogOpen(false);

    // if (newStation.tracks.length > seedTracks.length) {
    //   setNotification(`Your radio station "${name}" has been created with additional tracks to enhance variety.`);
    //   setTimeout(() => setNotification(null), 5000);
    // }
  };

  const handlePlayRadioStation = (stationId: string) => {
    const station = radioStations.find((s) => s.id === stationId);
    if (station) {
      const nextTrack = radioEngine.getNextTrack(stationId);
      if (nextTrack) {
        setCurrentTrack(nextTrack);
        setQueue(station.tracks);
        setIsPlaying(true);
      }
    }
  };

  const renderContent = () => {
    if (activeProvider === "local" && !radioStations.length) {
      return (
        <EmptyState
          icon={RadioIcon}
          title="Create your first radio station"
          description="Start by creating a custom radio station based on your favorite tracks"
          action={{
            label: "Create Radio Station",
            onClick: () => setIsCreateDialogOpen(true),
          }}
        />
      );
    }

    if (activeProvider === "spotify") {
      if (!isAuthenticated) {
        return (
          <EmptyState
            icon={RadioIcon}
            title="Connect to Spotify"
            description="Connect your Spotify account to access radio stations"
            action={{
              label: "Connect Spotify",
              onClick: login,
            }}
          />
        );
      }

      if (isLoading) {
        return (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
          </div>
        );
      }

      if (!recommendations?.tracks.length) {
        return (
          <EmptyState
            icon={RadioIcon}
            title="No recommendations found"
            description="Try playing more music to get personalized radio stations"
            action={{
              label: "Refresh",
              onClick: () => {
                /* TODO: Implement refresh logic */
              },
            }}
          />
        );
      }

      const spotifyItems: MediaItemProps[] = recommendations.tracks.map((track) => ({
        id: track.id,
        name: track.name,
        artist: track.artists[0].name,
        coverUrl: track.album.images[0]?.url,
        type: "radio",
        source: "spotify",
        trackCount: 50, // Assuming each radio station has 50 tracks
      }));

      return <MediaGrid items={spotifyItems} />;
    }

    const localItems: MediaItemProps[] = radioStations.map((station: RadioStation) => ({
      id: station.id,
      name: station.name,
      artist: "Custom Radio",
      coverUrl: station.coverUrl,
      type: "radio",
      source: "local",
      trackCount: station.trackCount,
    }));

    return <MediaGrid items={localItems} />;
  };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
      {notification && <div className="bg-yellow-500 text-white p-4 rounded-md mb-4">{notification}</div>}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold mb-2">Radio</h1>
          <p className="text-zinc-400">
            {activeProvider === "spotify" ? recommendations?.tracks.length || 0 : radioStations.length} station
            {(activeProvider === "spotify" ? recommendations?.tracks.length : radioStations.length) !== 1 ? "s" : ""}
          </p>
        </div>

        {activeProvider === "local" && (
          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition"
          >
            <Plus className="w-5 h-5" />
            Create Radio Station
          </button>
        )}
      </div>

      <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

      {renderContent()}

      <CreateRadioDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateStation={handleCreateRadioStation}
      />
    </div>
  );
};
